import type { StepConfig } from "~components/pages/listing/checkout/checkout-flow/checkout-flow-types"
import { findCurrentStepConfigIndex } from "~components/pages/listing/checkout/checkout-flow/checkout-flow-helpers"
import { useQueryParamString } from "~utils/use-query-param"
import { QUERY_STRING_PROPS } from "~config/constants"

export default function useStepByStepFlowState({
    stepConfigs,
}: {
    stepConfigs: StepConfig[]
}) {
    const firstStep = getFirstStep(stepConfigs)

    const [currentStep, setCurrentStep] = useQueryParamString({
        key: QUERY_STRING_PROPS.STEP,
        defaultValue: firstStep,
    })

    const { previousStep, nextStep, hasPreviousStep, hasNextStep } =
        getPreviousAndNextSteps({ currentStep, stepConfigs })

    const goToPreviousStep = () =>
        hasPreviousStep && setCurrentStep(previousStep)
    const goToNextStep = () => hasNextStep && setCurrentStep(nextStep)

    return {
        currentStep,
        goToStep: setCurrentStep,
        goToPreviousStep,
        goToNextStep,
        hasPreviousStep,
        hasNextStep,
        nextStep,
        previousStep,
        firstStep,
    }
}

function getPreviousAndNextSteps({
    currentStep,
    stepConfigs,
}: {
    currentStep: string | null
    stepConfigs: StepConfig[]
}) {
    const currentStepIndex = findCurrentStepConfigIndex({
        currentStep,
        stepConfigs,
    })
    const previousStep = stepConfigs[currentStepIndex - 1]?.step ?? null
    const nextStep = stepConfigs[currentStepIndex + 1]?.step ?? null
    const hasPreviousStep = Boolean(previousStep)
    const hasNextStep = Boolean(nextStep)

    return { previousStep, nextStep, hasPreviousStep, hasNextStep }
}

function getFirstStep(stepConfigs: StepConfig[]) {
    return stepConfigs[0]?.step ?? null
}
